import React, { useState, useEffect, useRef } from 'react';
import { useFormContext, Path, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { Search, Paperclip, X, Trash2 } from 'lucide-react';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { useAttachmentHandler } from '../../../../Hooks/useAttachmentHandler';
import SteelOrdersInfiniteSelect from '../../../../components/SteelOrdersInfiniteSelect';
import { NumericInput } from '../../../../components/NumericInput';
import PendencyCallout from '../../../../components/PendencyCallout';
import QuickStockPriceModal from '../../../../components/QuickStockPriceModal';
import ApiService from '../../../../../../../../services/ApiService';

import {
  Button,
  FormGroup,
  Input,
  Label,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  FormErrorMessage,
  HintText,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer
} from '../../../../style';
import {
  MaterialSearchInput,
  FormGridWithMargin,
  ObservationsFormGrid,
} from './FeedstockSection.style';

interface FeedstockSectionProps {
  itemPath: ItemPath;
}

const FeedstockSection: React.FC<FeedstockSectionProps> = ({ itemPath }) => {
  const { register, formState: { errors }, setValue, watch, control, setError, clearErrors } = useFormContext<OrderSchemaType>();
  const { attachments, attachmentError, fileInputRef, handleFileSelectClick, handleFileChange, handleRemoveAttachment, handleRemoveAllAttachments } = useAttachmentHandler({ fieldPath: `${itemPath}.feedstock.anexos` });
  const feedstockPath = `${itemPath}.feedstock`;
  const [selectedMaterials, setSelectedMaterials] = useState<{ id: number; name: string }[]>([]);
  const [selectedStock, setSelectedStock] = useState<{ id: number; name: string }[]>([]);
  const [showQuickPriceModal, setShowQuickPriceModal] = useState(false);

  const handleStockPricesSaved = (novos: { price: number; unitOfMeasurementId: number }[]) => {
    setSelectedStock(prev => {
      if (prev.length === 0) return prev;
      const s: any = prev[0];
      const prices = Array.isArray(s.prices) ? [...s.prices] : [];
      novos.forEach((n) => {
        const idx = prices.findIndex((p: any) => Number(p.unitOfMeasurementId) === Number(n.unitOfMeasurementId));
        const entry = { price: Number(n.price), unitOfMeasurementId: Number(n.unitOfMeasurementId), unit: '' };
        if (idx >= 0) prices[idx] = entry; else prices.push(entry);
      });
      const cobreUnidadeDoItem = novos.find((n) => Number(n.unitOfMeasurementId) === Number(itemUnitId));
      return [{
        ...s,
        prices,
        ...(cobreUnidadeDoItem
          ? { resolvedPrice: Number(cobreUnidadeDoItem.price), resolvedUnitId: Number(itemUnitId), hasPriceInUnit: true }
          : {}),
      }];
    });
  };

  const isSubItemPath = itemPath.includes('.subItems.');
  const unitSourcePath = isSubItemPath ? itemPath.split('.subItems.')[0] : itemPath;
  const itemUnitId = useWatch({ control, name: `${unitSourcePath}.unitOfMeasurementId` as any });
  const itemUnitName = useWatch({ control, name: `${unitSourcePath}.unitOfMeasurementName` as any });

  const resolveStockCost = (stockItem: any): number | null => {
    const unitId = Number(itemUnitId);
    if (!unitId || !stockItem) return null;
    const prices = Array.isArray(stockItem.prices) ? stockItem.prices : [];
    const resolved = prices.find((p: any) => Number(p.unitOfMeasurementId) === unitId);
    return resolved ? Number(resolved.price) : null;
  };

  const resolveStockMarkup = (stockItem: any): number | null => {
    if (!stockItem || stockItem.markup === undefined || stockItem.markup === null || isNaN(Number(stockItem.markup))) return null;
    return Number(stockItem.markup);
  };

  useEffect(() => {
    const existingMaterialId = watch((`${itemPath}.materialId`) as any) as number | undefined;
    if (selectedMaterials.length > 0) {
      setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, selectedMaterials[0].name);
      setValue((`${itemPath}.materialId`) as any, selectedMaterials[0].id, { shouldDirty: true, shouldValidate: false });
      setSelectedStock([]);
      setValue(`${feedstockPath}.stockCodeId` as Path<OrderSchemaType>, undefined, { shouldDirty: true, shouldValidate: false });
      setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
    } else {
      if (existingMaterialId === undefined || existingMaterialId === null) {
        setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, '');
        setValue((`${itemPath}.materialId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
        setSelectedStock([]);
        setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
        setValue((`${itemPath}.stockStandards`) as any, undefined, { shouldDirty: false, shouldValidate: false });
        setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
      }
    }
  }, [selectedMaterials, setValue, feedstockPath, itemPath, watch]);

  const watchedMaterialId = useWatch({ control, name: (`${itemPath}.materialId` as any) });
  const watchedMateriaPrima = useWatch({ control, name: (`${feedstockPath}.materiaPrima` as any) });

  useEffect(() => {
    const idRaw: any = watchedMaterialId as any;
    const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
    const nameRaw: any = watchedMateriaPrima as any;
    const name = typeof nameRaw === 'string' ? nameRaw : String(nameRaw || '');
    if (!isNaN(id) && id && selectedMaterials.length === 0) {
      setSelectedMaterials([{ id: Number(id), name: String(name || '') }]);
      if (!name || String(name).trim() === '') {
        (async () => {
          try {
            // fetch cru não leva o Bearer do interceptor → 401 silencioso na hidratação
            const res = await ApiService.steelOrders.getMaterials({ id: Number(id) });
            const data = res.data;
            const arr = Array.isArray(data?.materials) ? data.materials : Array.isArray(data) ? data : [];
            const first = arr[0];
            const fetchedName = (first && typeof first === 'object') ? (first.name ?? '') : String(first ?? '');
            if (fetchedName) {
              setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, fetchedName, { shouldDirty: false, shouldValidate: false });
              setSelectedMaterials([{ id: Number(id), name: fetchedName }]);

            }
          } catch (e) { void e; }
        })();
      } else { void 0; }
    }
  }, [watchedMaterialId, watchedMateriaPrima, selectedMaterials.length, setValue, feedstockPath]);

  // Achado 16 — chave estoque|unidade: só descarta o preço quando a seleção MUDA de fato;
  // refetch da hidratação (mesma chave) preserva/recompõe o valor pontual salvo.
  const prevResolveKeyRef = useRef<string | 'uninit'>('uninit');
  useEffect(() => {
    if (selectedStock.length > 0) {
      setValue((`${itemPath}.stockId`) as any, selectedStock[0].id, { shouldDirty: true, shouldValidate: false });
      setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, selectedStock[0].name, { shouldDirty: true, shouldValidate: false });

      const stockItem: any = selectedStock[0];
      const stockCost = resolveStockCost(stockItem);
      const stockMarkup = resolveStockMarkup(stockItem);
      setValue(`${feedstockPath}.stockCostInUnit` as any, stockCost ?? undefined, { shouldDirty: false, shouldValidate: false });
      setValue(`${feedstockPath}.stockMarkup` as any, stockMarkup ?? undefined, { shouldDirty: false, shouldValidate: false });
      const derived = stockCost != null && stockMarkup != null
        ? parseFloat((stockCost * stockMarkup).toFixed(4))
        : null;
      const key = `${stockItem.id}|${Number(itemUnitId) || 0}`;
      const changed = prevResolveKeyRef.current !== 'uninit' && prevResolveKeyRef.current !== key;
      prevResolveKeyRef.current = key;

      if (derived != null) {
        setValue(`${itemPath}.priceDetails.unitPrice` as any, derived, { shouldDirty: true });
      } else if (changed) {
        setValue(`${itemPath}.priceDetails.unitPrice` as any, '', { shouldDirty: true });
      } else {
        const current = Number(watch(`${itemPath}.priceDetails.unitPrice` as any)) || 0;
        if (!current) {
          const persistedTotal = Number(watch(`${itemPath}.itemPrice` as any)) || 0;
          const qty = Number(watch(`${feedstockPath}.quantidade` as any)) || 0;
          if (persistedTotal > 0 && qty > 0) {
            setValue(`${itemPath}.priceDetails.unitPrice` as any, parseFloat((persistedTotal / qty).toFixed(4)), { shouldDirty: false });
          }
        }
      }
      if (stockItem.ncmId !== undefined && stockItem.ncmId !== null) {
        setValue(`${itemPath}.ncmId` as any, stockItem.ncmId, { shouldDirty: true });
      }
      if ('standardsMet' in stockItem) {
        setValue(`${itemPath}.stockStandards` as any, stockItem.standardsMet || undefined, { shouldDirty: false, shouldValidate: false });
      }
      if (Array.isArray(stockItem.quantities)) {
        setValue(`${feedstockPath}.stockQuantities` as any, stockItem.quantities, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [selectedStock, setValue, feedstockPath, itemPath, itemUnitId, watch]);

  const watchedStockId = useWatch({ control, name: (`${itemPath}.stockId`) as any });
  const watchedStockDescription = useWatch({ control, name: `${feedstockPath}.stockDescription` as any });
  const watchedQuantidade = useWatch({ control, name: `${feedstockPath}.quantidade` as any });

  const isSubItem = itemPath.includes('.subItems.');

  // Achado 16 — total da MP persiste no itemPrice do subitem (vai no payload do back);
  // é dele que a reabertura recompõe o valor pontual (itemPrice ÷ quantidade).
  const watchedUnitPrice = useWatch({ control, name: `${itemPath}.priceDetails.unitPrice` as any });
  useEffect(() => {
    if (!isSubItem) return; // no nível de item, o useItemSubtotalCalculator é a fonte
    const up = Number(watchedUnitPrice) || 0;
    const qty = Number(watchedQuantidade) || 0;
    const total = up > 0 && qty > 0 ? parseFloat((up * qty).toFixed(2)) : undefined;
    const current = watch(`${itemPath}.itemPrice` as any);
    if (current !== total) {
      setValue(`${itemPath}.itemPrice` as any, total, { shouldDirty: true, shouldValidate: false });
    }
  }, [watchedUnitPrice, watchedQuantidade, isSubItem, itemPath, setValue, watch]);

  useEffect(() => {
    const idRaw: any = watchedStockId as any;
    const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
    const nameRaw: any = watchedStockDescription as any;
    const name = typeof nameRaw === 'string' ? nameRaw : String(nameRaw || '');
    if (!isNaN(id) && id && selectedStock.length === 0) {
      setSelectedStock([{ id: Number(id), name: String(name || '') }]);
      (async () => {
        try {
          const materialId = selectedMaterials[0]?.id || watch((`${itemPath}.materialId`) as any);
          if (materialId) {
            // fetch cru não leva o Bearer do interceptor → 401 silencioso e o stock ficava sem prices[]
            const res = await ApiService.steelOrders.getStocks({ materialId, id: Number(id) });
            const data = res.data;
            const arr = Array.isArray(data?.stocks) ? data.stocks : Array.isArray(data) ? data : [];
            if (arr.length > 0) {
              const stockItem = arr[0];
              const stockName = stockItem.description || stockItem.name || name || '';
              if (!name || String(name).trim() === '') {
                setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, stockName, { shouldDirty: true, shouldValidate: false });
              }
              setSelectedStock([{ ...stockItem, id: Number(id), name: stockName }]);
            }
          }
        } catch (e) { void e; }
      })();
    }
  }, [watchedStockId, watchedStockDescription, selectedStock.length, setValue, feedstockPath, selectedMaterials, watch, itemPath]);

  useEffect(() => {
    const qty = Number(watchedQuantidade);

    if (isSubItem) {
      if (!isNaN(qty) && qty > 0) {
        setValue((`${itemPath}.quantity`) as any, qty, { shouldDirty: true, shouldValidate: false });
      } else if (watchedQuantidade === '' || watchedQuantidade === undefined || watchedQuantidade === null) {
        setValue((`${itemPath}.quantity`) as any, undefined, { shouldDirty: true, shouldValidate: false });
      }
    }

    if (selectedStock.length > 0) {
      const stockItem = selectedStock[0] as any;
      const unit = stockItem.measurementUnit;
      if (unit) {
        setValue(`${feedstockPath}.unit` as any, unit, { shouldDirty: false, shouldValidate: false });
      }

      const available = Array.isArray(stockItem.quantities)
        ? stockItem.quantities.find((q: any) => Number(q?.unitOfMeasurementId) === Number(itemUnitId))
        : undefined;
      if (available && available.quantity != null && !isNaN(qty) && qty > Number(available.quantity)) {
        setError(`${feedstockPath}.quantidade` as any, {
          type: 'manual',
          message: `Máximo em estoque${available.unit ? ` (${available.unit})` : ''}: ${available.quantity}`,
        });
      } else {
        clearErrors(`${feedstockPath}.quantidade` as any);
      }
    } else {
      clearErrors(`${feedstockPath}.quantidade` as any);
    }
  }, [watchedQuantidade, selectedStock, setValue, itemPath, feedstockPath, itemUnitId, setError, clearErrors]);

  return (
    <>
      <FormGridWithMargin columns={4}>
        <SteelOrdersInfiniteSelect
          label="Material"
          items={selectedMaterials}
          setItems={setSelectedMaterials}
          endpoint="materials"
          placeholder="Procure por nome ou código"
          onCleared={() => {
            setValue((`${itemPath}.materialId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue(`${feedstockPath}.materiaPrima` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
            setSelectedStock([]);
            setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${itemPath}.stockStandards`) as any, undefined, { shouldDirty: false, shouldValidate: false });
            setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
          }}
        />
        <SteelOrdersInfiniteSelect
          label="Estoque"
          items={selectedStock}
          setItems={setSelectedStock}
          endpoint="stocks"
          placeholder="Selecione um item do estoque"
          searchPlaceholder={selectedMaterials[0]?.id ? 'Nenhum estoque encontrado' : 'Selecione um material primeiro'}
          isDisabled={!selectedMaterials[0]?.id}
          additionalParams={selectedMaterials[0]?.id ? { materialId: selectedMaterials[0].id } : {}}
          onCleared={() => {
            setValue((`${itemPath}.stockId`) as any, undefined, { shouldDirty: true, shouldValidate: false });
            setValue((`${itemPath}.stockStandards`) as any, undefined, { shouldDirty: false, shouldValidate: false });
            setValue(`${feedstockPath}.stockDescription` as Path<OrderSchemaType>, '', { shouldDirty: true, shouldValidate: false });
          }}
          key={selectedMaterials[0]?.id ? `stocks-${selectedMaterials[0].id}` : 'stocks-disabled'}
        />
        {selectedStock.length > 0 && !itemUnitId && (
          <HintText>Selecione a Unidade do item para formar o preço da matéria-prima</HintText>
        )}
        {selectedStock.length > 0 && !!itemUnitId && resolveStockCost(selectedStock[0]) === null && (
          <PendencyCallout
            message={itemUnitName
              ? `Estoque sem custo em ${itemUnitName} no cadastro`
              : 'Estoque sem custo na unidade do item no cadastro'}
            actionLabel="+ Cadastrar custo no estoque"
            onAction={() => setShowQuickPriceModal(true)}
          />
        )}
        {selectedStock.length > 0 && !!itemUnitId && resolveStockCost(selectedStock[0]) !== null
          && resolveStockMarkup(selectedStock[0]) === null && (
          <PendencyCallout message="Estoque sem markup — defina o markup no cadastro de estoque para formar o preço" />
        )}
        {showQuickPriceModal && selectedStock.length > 0 && !!itemUnitId && (
          <QuickStockPriceModal
            isOpen={showQuickPriceModal}
            onClose={() => setShowQuickPriceModal(false)}
            stockId={Number((selectedStock[0] as any).id)}
            stockName={selectedStock[0].name}
            missingUnitName={itemUnitName || undefined}
            onSaved={handleStockPricesSaved}
          />
        )}
        {isSubItem && (
          <FormGroup>
            <Label>Quantidade{itemUnitName ? ` (${itemUnitName})` : ''}</Label>
            <NumericInput
              name={`${feedstockPath}.quantidade`}
              value={(watch(`${feedstockPath}.quantidade` as any) as any) || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${feedstockPath}.quantidade` as any, e.target.value, { shouldDirty: true })}
              onBlur={() => { }}
              hasError={!!get(errors, `${feedstockPath}.quantidade`)}
            />
            {get(errors, `${feedstockPath}.quantidade`) && (
              <FormErrorMessage>{(get(errors, `${feedstockPath}.quantidade`) as any)?.message as string}</FormErrorMessage>
            )}
          </FormGroup>
        )}
        <FormGroup>
          <Label>Arquivo</Label>
          <AttachmentInputContainer>
            <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
              <Paperclip size={16} />
              <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
            </AttachmentTrigger>
            {attachments.length > 0 && (
              <TrashIconContainer
                as="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveAllAttachments();
                }}
              >
                <Trash2 size={16} />
              </TrashIconContainer>
            )}
          </AttachmentInputContainer>
          {attachments.length > 0 && (
            <AttachmentList>
              {attachments.map((file, index) => (
                <AttachmentListItem key={index}>
                  {file.name}
                  <RemoveAttachmentButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAttachment(index);
                    }}
                  >
                    <X size={12} />
                  </RemoveAttachmentButton>
                </AttachmentListItem>
              ))}
            </AttachmentList>
          )}
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
          />
          {attachmentError && <FormErrorMessage>{attachmentError}</FormErrorMessage>}
        </FormGroup>
      </FormGridWithMargin>

      <ObservationsFormGrid>
        <FormGroup>
          <Label>Observações</Label>
          <Input {...register(`${feedstockPath}.observacoes` as Path<OrderSchemaType>)} placeholder="Digite aqui" />
        </FormGroup>
      </ObservationsFormGrid>
    </>
  );
};

export default FeedstockSection;